import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  loadRaidBridgeStatus,
  revokeRaidBridgeToken,
  rotateRaidBridgeToken,
} from '../api/raidBridgeApi.js';
import {
  applyRaidBridgeUserscriptConfig,
  compareRaidBridgeVersions,
  copyConfiguredRaidBridgeUserscript,
  extractRaidBridgeToken,
  openRaidBridgeUserscriptInstaller,
  probeRaidBridgeUserscript,
  RAID_BRIDGE_USERSCRIPT_VERSION,
} from '../api/raidBridgeInstaller.js';

const EMPTY_STATUS = {
  unionId: '',
  unionName: '',
  viewerRole: '',
  canManage: false,
  activeSeasonNo: null,
  seasons: [],
  currentRecordCount: 0,
  activeToken: null,
  recentBatches: [],
};

const EMPTY_INSTALLER = {
  checking: true,
  installed: false,
  configured: false,
  version: '',
  seasonNo: null,
  tokenRevision: '',
  detectedVersions: [],
  multipleVersions: false,
};

const SEASON_STORAGE_PREFIX = 'dororaid_collector_season_v1:';
const TOKEN_REVISION_STORAGE_PREFIX = 'dororaid_collector_token_revision_v1:';

function errorText(error, fallback) {
  const raw = error instanceof Error ? error.message : String(error || '');
  if (raw === 'tampermonkey_bridge_timeout') return 'Tampermonkey 수집기 응답이 없습니다. 수집기가 활성화됐는지 확인하고 페이지를 새로고침해주세요.';
  if (raw === 'invalid_bridge_token') return '유효한 수집 토큰을 확인하지 못했습니다.';
  if (raw === 'outdated_bridge_token') return '구형 토큰입니다. 레이드 수집기 화면에서 새 공용 토큰을 저장해주세요.';
  if (raw === 'raid_bridge_upgrade_required') return '설치된 수집기가 오래됐습니다. 최신 수집기로 업데이트한 뒤 다시 실행해주세요.';
  if (raw === 'invalid_bridge_payload') return '공용 토큰은 저장됐지만 현재 수집기의 요청 형식이 오래됐습니다. 수집기를 업데이트한 뒤 다시 실행해주세요.';
  if (raw === 'invalid_season_no') return '수집할 레이드 차수를 선택해주세요.';
  if (raw.startsWith('userscript_load_failed_')) return '설치 스크립트를 불러오지 못했습니다.';
  if (raw === 'NotAllowedError') return '클립보드 권한이 차단되었습니다.';
  return raw || fallback;
}

function seasonStorageKey(unionId) {
  return unionId ? `${SEASON_STORAGE_PREFIX}${unionId}` : '';
}

function tokenRevisionStorageKey(unionId) {
  return unionId ? `${TOKEN_REVISION_STORAGE_PREFIX}${unionId}` : '';
}

function readStoredSeason(unionId) {
  const key = seasonStorageKey(unionId);
  if (!key) return null;
  try {
    const value = Number(window.localStorage.getItem(key));
    return Number.isInteger(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
}

function writeStoredSeason(unionId, seasonNo) {
  const key = seasonStorageKey(unionId);
  if (!key) return;
  try { window.localStorage.setItem(key, String(seasonNo)); } catch {}
}

function readStoredTokenRevision(unionId) {
  const key = tokenRevisionStorageKey(unionId);
  if (!key) return '';
  try { return String(window.localStorage.getItem(key) || '').trim(); }
  catch { return ''; }
}

function writeStoredTokenRevision(unionId, revision) {
  const key = tokenRevisionStorageKey(unionId);
  const normalized = String(revision || '').trim();
  if (!key || !normalized) return;
  try { window.localStorage.setItem(key, normalized); } catch {}
}

function clearStoredTokenRevision(unionId) {
  const key = tokenRevisionStorageKey(unionId);
  if (!key) return;
  try { window.localStorage.removeItem(key); } catch {}
}

function chooseSeason(status, currentSeasonNo = null) {
  const available = (status.seasons || []).map((season) => Number(season.seasonNo));
  const isAvailable = (value) => Number.isInteger(Number(value)) && available.includes(Number(value));
  if (isAvailable(currentSeasonNo)) return Number(currentSeasonNo);
  const stored = readStoredSeason(status.unionId);
  if (isAvailable(stored)) return stored;
  if (isAvailable(status.activeSeasonNo)) return Number(status.activeSeasonNo);
  return available[0] || null;
}

export function useRaidBridge() {
  const [status, setStatus] = useState(EMPTY_STATUS);
  const [selectedSeasonNo, setSelectedSeasonNo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState('');
  const [manualTokenInput, setManualTokenInput] = useState('');
  const [notice, setNotice] = useState({ type: '', message: '' });
  const [installer, setInstaller] = useState(EMPTY_INSTALLER);
  const [browserTokenRevision, setBrowserTokenRevision] = useState('');

  const manualToken = extractRaidBridgeToken(manualTokenInput);
  const sharedToken = String(status.activeToken?.token || '').trim();
  const sharedTokenRevision = String(status.activeToken?.revision || status.activeToken?.id || '').trim();
  const sharedTokenReady = Boolean(status.activeToken?.shareReady && sharedToken && sharedTokenRevision);
  const installedTokenRevision = String(installer.tokenRevision || browserTokenRevision || '').trim();
  const installerVersionCurrent = Boolean(
    installer.installed
    && installer.version
    && compareRaidBridgeVersions(installer.version, RAID_BRIDGE_USERSCRIPT_VERSION) >= 0,
  );
  const installerUpdateRequired = Boolean(installer.installed && !installerVersionCurrent);
  const tokenOutdated = Boolean(
    installer.installed
    && installer.configured
    && sharedTokenReady
    && installedTokenRevision !== sharedTokenRevision,
  );
  const tokenCurrent = Boolean(
    installer.installed
    && installer.configured
    && sharedTokenReady
    && installedTokenRevision === sharedTokenRevision,
  );
  const configuredForSeason = Boolean(
    tokenCurrent
    && selectedSeasonNo
    && Number(installer.seasonNo) === Number(selectedSeasonNo),
  );
  const collectorReady = Boolean(configuredForSeason);

  const selectedSeason = useMemo(
    () => status.seasons.find((season) => season.seasonNo === selectedSeasonNo) || null,
    [selectedSeasonNo, status.seasons],
  );

  const acceptStatus = useCallback((nextStatus) => {
    setStatus(nextStatus);
    setSelectedSeasonNo((current) => chooseSeason(nextStatus, current));
    setBrowserTokenRevision(readStoredTokenRevision(nextStatus.unionId));
    return nextStatus;
  }, []);

  const refresh = useCallback(async ({ keepNotice = false } = {}) => {
    setLoading(true);
    try {
      acceptStatus(await loadRaidBridgeStatus());
      if (!keepNotice) setNotice({ type: '', message: '' });
    } catch (error) {
      setNotice({ type: 'error', message: errorText(error, '수집기 상태를 불러오지 못했습니다.') });
    } finally {
      setLoading(false);
    }
  }, [acceptStatus]);

  const probeInstaller = useCallback(async () => {
    setInstaller((current) => ({ ...current, checking: true }));
    try {
      const result = await probeRaidBridgeUserscript();
      setInstaller({ checking: false, ...result });
      return result;
    } catch {
      const next = { ...EMPTY_INSTALLER, checking: false };
      setInstaller(next);
      return next;
    }
  }, []);

  useEffect(() => {
    let active = true;
    loadRaidBridgeStatus()
      .then((next) => { if (active) acceptStatus(next); })
      .catch((error) => { if (active) setNotice({ type: 'error', message: errorText(error, '수집기 상태를 불러오지 못했습니다.') }); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [acceptStatus]);

  useEffect(() => {
    probeInstaller();
    const handleReady = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'DORORAID_BRIDGE_READY') probeInstaller();
    };
    const handleFocus = () => probeInstaller();
    window.addEventListener('message', handleReady);
    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('message', handleReady);
      window.removeEventListener('focus', handleFocus);
    };
  }, [probeInstaller]);

  const selectSeason = useCallback((seasonNo) => {
    const normalized = Number(seasonNo);
    const exists = status.seasons.some((season) => season.seasonNo === normalized);
    if (!Number.isInteger(normalized) || !exists) return false;
    setSelectedSeasonNo(normalized);
    writeStoredSeason(status.unionId, normalized);
    setNotice({ type: '', message: '' });
    return true;
  }, [status.seasons, status.unionId]);

  useEffect(() => {
    if (selectedSeasonNo && status.unionId) writeStoredSeason(status.unionId, selectedSeasonNo);
  }, [selectedSeasonNo, status.unionId]);

  const rotate = useCallback(async () => {
    if (working) return false;
    setWorking('rotate');
    setNotice({ type: '', message: '' });
    try {
      await rotateRaidBridgeToken('Union Shared Collector');
      const nextStatus = acceptStatus(await loadRaidBridgeStatus());
      setNotice({
        type: 'success',
        message: '공용 토큰을 재발급했습니다. 모든 관리자는 자신의 화면에서 공용 토큰 저장을 다시 눌러주세요.',
      });
      return nextStatus;
    } catch (error) {
      setNotice({ type: 'error', message: errorText(error, '공용 토큰 재발급에 실패했습니다.') });
      return false;
    } finally {
      setWorking('');
    }
  }, [acceptStatus, working]);

  const revoke = useCallback(async () => {
    if (working) return false;
    setWorking('revoke');
    setNotice({ type: '', message: '' });
    try {
      await revokeRaidBridgeToken();
      clearStoredTokenRevision(status.unionId);
      setBrowserTokenRevision('');
      acceptStatus(await loadRaidBridgeStatus());
      setNotice({ type: 'success', message: '공용 토큰을 해제했습니다. 모든 관리자 PC에서 수집이 중지됩니다.' });
      return true;
    } catch (error) {
      setNotice({ type: 'error', message: errorText(error, '공용 토큰 해제에 실패했습니다.') });
      return false;
    } finally {
      setWorking('');
    }
  }, [acceptStatus, status.unionId, working]);

  const applyToken = useCallback(async ({ token, tokenRevision = '', workingKey, successMessage }) => {
    if (working || !selectedSeasonNo) return false;
    const normalizedToken = extractRaidBridgeToken(token);
    if (!normalizedToken) {
      setNotice({ type: 'error', message: errorText(new Error('invalid_bridge_token')) });
      return false;
    }

    setWorking(workingKey);
    setNotice({ type: '', message: '' });
    try {
      const result = await applyRaidBridgeUserscriptConfig({
        token: normalizedToken,
        tokenRevision,
        seasonNo: selectedSeasonNo,
      });
      const appliedRevision = String(result.tokenRevision || tokenRevision || '').trim();
      if (appliedRevision && status.unionId) {
        writeStoredTokenRevision(status.unionId, appliedRevision);
        setBrowserTokenRevision(appliedRevision);
      }
      const verified = await probeRaidBridgeUserscript();
      setInstaller({
        checking: false,
        installed: true,
        ...result,
        ...verified,
        tokenRevision: String(verified.tokenRevision || appliedRevision || '').trim(),
      });
      setManualTokenInput('');
      setNotice({ type: 'success', message: successMessage || `공용 토큰과 ${selectedSeasonNo}차를 이 PC 수집기에 저장했습니다.` });
      return true;
    } catch (error) {
      setNotice({ type: 'error', message: errorText(error, '공용 토큰 저장에 실패했습니다.') });
      await probeInstaller();
      return false;
    } finally {
      setWorking('');
    }
  }, [probeInstaller, selectedSeasonNo, status.unionId, working]);

  const applySharedToken = useCallback(
    () => applyToken({
      token: sharedToken,
      tokenRevision: sharedTokenRevision,
      workingKey: 'apply-shared',
      successMessage: `유니온 공용 토큰과 ${selectedSeasonNo}차를 이 PC 수집기에 저장했습니다.`,
    }),
    [applyToken, selectedSeasonNo, sharedToken, sharedTokenRevision],
  );

  const applyManualToken = useCallback(
    () => applyToken({ token: manualTokenInput, workingKey: 'manual', successMessage: `입력한 토큰과 ${selectedSeasonNo}차를 저장했습니다.` }),
    [applyToken, manualTokenInput, selectedSeasonNo],
  );

  const pasteTokenFromClipboard = useCallback(async () => {
    if (working) return false;
    try {
      const clipboardText = await navigator.clipboard.readText();
      const token = extractRaidBridgeToken(clipboardText);
      if (!token) throw new Error('invalid_bridge_token');
      setManualTokenInput(token);
      return applyToken({ token, workingKey: 'clipboard', successMessage: `클립보드 토큰과 ${selectedSeasonNo}차를 저장했습니다.` });
    } catch (error) {
      setNotice({ type: 'error', message: errorText(error, '클립보드 토큰을 읽지 못했습니다.') });
      return false;
    }
  }, [applyToken, selectedSeasonNo, working]);

  const applySelectedSeason = useCallback(async () => {
    if (!tokenCurrent) return applySharedToken();
    if (working || !selectedSeasonNo) return false;
    setWorking('season');
    setNotice({ type: '', message: '' });
    try {
      const result = await applyRaidBridgeUserscriptConfig({ seasonNo: selectedSeasonNo });
      const verified = await probeRaidBridgeUserscript();
      setInstaller({
        checking: false,
        installed: true,
        ...result,
        ...verified,
        tokenRevision: String(verified.tokenRevision || installedTokenRevision || '').trim(),
      });
      setNotice({ type: 'success', message: `수집 차수를 ${selectedSeasonNo}차로 변경했습니다.` });
      return true;
    } catch (error) {
      setNotice({ type: 'error', message: errorText(error, '수집 차수 적용에 실패했습니다.') });
      await probeInstaller();
      return false;
    } finally {
      setWorking('');
    }
  }, [applySharedToken, installedTokenRevision, probeInstaller, selectedSeasonNo, tokenCurrent, working]);

  const copyConfiguredUserscript = useCallback(async () => {
    if (working || !sharedTokenReady || !selectedSeasonNo) return false;
    setWorking('script');
    setNotice({ type: '', message: '' });
    try {
      await copyConfiguredRaidBridgeUserscript({
        token: sharedToken,
        tokenRevision: sharedTokenRevision,
        seasonNo: selectedSeasonNo,
      });
      setNotice({ type: 'success', message: '공용 토큰과 선택 차수가 포함된 설치 스크립트를 복사했습니다.' });
      return true;
    } catch (error) {
      setNotice({ type: 'error', message: errorText(error, '설치 스크립트 복사에 실패했습니다.') });
      return false;
    } finally {
      setWorking('');
    }
  }, [selectedSeasonNo, sharedToken, sharedTokenReady, sharedTokenRevision, working]);

  return {
    ...status,
    selectedSeasonNo,
    selectedSeason,
    loading,
    working,
    manualTokenInput,
    manualTokenValid: Boolean(manualToken),
    notice,
    installer,
    latestInstallerVersion: RAID_BRIDGE_USERSCRIPT_VERSION,
    installerVersionCurrent,
    installerUpdateRequired,
    sharedToken,
    sharedTokenReady,
    sharedTokenRevision,
    installedTokenRevision,
    tokenCurrent,
    tokenOutdated,
    configuredForSeason,
    collectorReady,
    refresh,
    rotate,
    revoke,
    probeInstaller,
    selectSeason,
    applySharedToken,
    applyManualToken,
    pasteTokenFromClipboard,
    setManualTokenInput,
    applyCurrentSeason: applySelectedSeason,
    applySelectedSeason,
    copyConfiguredUserscript,
    openInstaller: openRaidBridgeUserscriptInstaller,
  };
}
